import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert, Card } from "react-bootstrap";
import SafetyParticolariService from "../../../services/safety/SafetyParticolariService";
import LavoratoreSearchPicker from "../LavoratoreSearchPicker/LavoratoreSearchPicker";

const EMPTY = {
    id_particolare: "",
    id_tipo_part: "",
    id_lavoratore: "",
    stato: "ATTIVO",
    data_inizio: "",
    data_fine: "",
    esito_valutazione: "IN_CORSO",
    responsabile: "",
    data_valutazione: "",
    data_scadenza_valutazione: "",
    periodicita_mesi: "",
    vincoli_organizzativi: "",
    note: "",
};

const STATI = [
    { value: "ATTIVO", label: "Attivo" },
    { value: "SOSPESO", label: "Sospeso" },
    { value: "CHIUSO", label: "Chiuso" },
];

const ESITI = [
    { value: "IN_CORSO", label: "Valutazione in corso" },
    { value: "IDONEO", label: "Idoneo" },
    { value: "IDONEO_CON_LIMITAZIONI", label: "Idoneo con limitazioni" },
    { value: "NON_IDONEO", label: "Non idoneo" },
];

const HELP = {
    GESTANTE: "Valutazione rischi specifica art. 25-26 D.Lgs. 81/08. Adempiere obblighi di tutela maternità.",
    ALLATTAMENTO: "Misure per lavoratrice in allattamento: orari, postazione, rischi specifici.",
    MINORE: "Divieti e prescrizioni art. 31 D.Lgs. 81/08. Valutazione obbligatoria prima dell'impiego.",
    LAVORO_NOTTURNO: "Sorveglianza sanitaria e valutazione rischi turni notturni (art. 36, 41).",
    LAVORATORE_ISOLATO: "Procedure di soccorso, comunicazione e valutazione rischi in solitudine (art. 45).",
    DISABILE_L104: "Valutazione personalizzata e adeguamento postazione/mansione (art. 27, L. 104/1992).",
};

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

function apiErr(err, fallback) {
    if (!err) return fallback;
    if (typeof err === "string") return err;
    const m = err.message || err.messaggio || err.error?.message || err.error;
    return (typeof m === "string" && m.trim()) ? m : fallback;
}

function statoBadge(stato, giorni) {
    const map = {
        SCADUTO:     { bg: "danger",  text: "light", label: "Revisione scaduta" },
        IN_SCADENZA: { bg: "warning", text: "dark",  label: "Revisione in scadenza" },
        MANCANTE:    { bg: "danger",  text: "light", label: "Valutazione mancante" },
        VALIDO:      { bg: "success", text: "light", label: "In regola" },
        PERMANENTE:  { bg: "light",   text: "dark",  label: "Senza revisione" },
        CHIUSO:      { bg: "secondary", text: "light", label: "Chiuso" },
    };
    const m = map[stato] || map.PERMANENTE;
    return (
        <span>
            <Badge bg={m.bg} text={m.text} className={m.bg === "light" ? "border" : undefined}>{m.label}</Badge>
            {typeof giorni === "number" && (stato === "SCADUTO" || stato === "IN_SCADENZA") && (
                <span className="text-muted small ms-1">
                    ({giorni < 0 ? `${Math.abs(giorni)} gg fa` : `tra ${giorni} gg`})
                </span>
            )}
        </span>
    );
}

function esitoBadge(esito) {
    const map = {
        IDONEO: { bg: "success", label: "Idoneo" },
        IDONEO_CON_LIMITAZIONI: { bg: "warning", label: "Con limitazioni" },
        NON_IDONEO: { bg: "danger", label: "Non idoneo" },
        IN_CORSO: { bg: "secondary", label: "In corso" },
    };
    const m = map[esito] || map.IN_CORSO;
    return <Badge bg={m.bg} text={m.bg === "warning" ? "dark" : "light"}>{m.label}</Badge>;
}

export default function ParticolariPanel({ idAzienda, settore }) {
    const [tipi, setTipi] = useState([]);
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [soloAttivi, setSoloAttivi] = useState(true);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const parseJson = async (resp) => (resp && typeof resp.json === "function" ? resp.json() : resp);

    const loadTipi = useCallback(async () => {
        if (!idAzienda) { setTipi([]); return; }
        try {
            const data = await parseJson(await SafetyParticolariService.getTipi(idAzienda));
            setTipi(Array.isArray(data) ? data : []);
        } catch {
            setTipi([]);
        }
    }, [idAzienda]);

    const loadStats = useCallback(async () => {
        if (!idAzienda) return;
        try {
            const data = await parseJson(await SafetyParticolariService.stats(idAzienda));
            setStats(data && !data.message ? data : null);
        } catch {
            setStats(null);
        }
    }, [idAzienda]);

    const loadItems = useCallback(async () => {
        if (!idAzienda) { setItems([]); return; }
        setLoading(true);
        setError(null);
        try {
            const data = await parseJson(await SafetyParticolariService.byAzienda(idAzienda, soloAttivi));
            if (data?.message || data?.error) throw new Error(data.message || data.error);
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            setItems([]);
            const m = apiErr(err, "");
            setError(
                m.includes("NetworkError") || m.includes("Failed to fetch")
                    ? "API non raggiungibile. Verifica deploy: SafetyParticolariController + Service, auth.json, SQL 2026-06-16."
                    : (m || "Errore caricamento lavoratori particolari.")
            );
        } finally {
            setLoading(false);
        }
    }, [idAzienda, soloAttivi]);

    useEffect(() => { loadTipi(); }, [loadTipi]);
    useEffect(() => { loadStats(); loadItems(); }, [loadStats, loadItems]);

    const openForm = (item = null) => {
        if (item) {
            setForm({
                id_particolare: item.id_particolare,
                id_tipo_part: item.id_tipo_part,
                id_lavoratore: item.id_lavoratore,
                stato: item.stato || "ATTIVO",
                data_inizio: item.data_inizio ? String(item.data_inizio).substring(0, 10) : "",
                data_fine: item.data_fine ? String(item.data_fine).substring(0, 10) : "",
                esito_valutazione: item.esito_valutazione || "IN_CORSO",
                responsabile: item.responsabile || "",
                data_valutazione: item.data_valutazione ? String(item.data_valutazione).substring(0, 10) : "",
                data_scadenza_valutazione: item.data_scadenza_valutazione ? String(item.data_scadenza_valutazione).substring(0, 10) : "",
                periodicita_mesi: item.periodicita_mesi ?? "",
                vincoli_organizzativi: item.vincoli_organizzativi || "",
                note: item.note || "",
            });
        } else {
            setForm({ ...EMPTY, data_inizio: new Date().toISOString().substring(0, 10) });
        }
        setFile(null);
        setShow(true);
        setError(null);
        setMsg(null);
    };

    const onTipoChange = (idTipo) => {
        const t = tipi.find(x => String(x.id_tipo_part) === String(idTipo));
        setForm(f => ({
            ...f,
            id_tipo_part: idTipo,
            periodicita_mesi: t?.periodicita_mesi ?? f.periodicita_mesi,
        }));
    };

    const submit = async () => {
        if (!idAzienda || !form.id_lavoratore || !form.id_tipo_part || !form.data_inizio) {
            setError("Compila lavoratore, condizione particolare e data inizio.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const fd = new FormData();
            fd.append("id_azienda", idAzienda);
            fd.append("id_lavoratore", form.id_lavoratore);
            fd.append("id_tipo_part", form.id_tipo_part);
            fd.append("stato", form.stato);
            fd.append("data_inizio", form.data_inizio);
            if (form.data_fine) fd.append("data_fine", form.data_fine);
            fd.append("esito_valutazione", form.esito_valutazione);
            if (form.responsabile) fd.append("responsabile", form.responsabile);
            if (form.data_valutazione) fd.append("data_valutazione", form.data_valutazione);
            if (form.data_scadenza_valutazione) fd.append("data_scadenza_valutazione", form.data_scadenza_valutazione);
            if (form.periodicita_mesi) fd.append("periodicita_mesi", form.periodicita_mesi);
            if (form.vincoli_organizzativi) fd.append("vincoli_organizzativi", form.vincoli_organizzativi);
            if (form.note) fd.append("note", form.note);
            if (file) fd.append("file", file);

            const isEdit = !!form.id_particolare;
            if (isEdit) fd.append("id_particolare", form.id_particolare);

            const resp = isEdit
                ? await SafetyParticolariService.update(fd)
                : await SafetyParticolariService.create(fd);
            const data = await parseJson(resp);
            if (data?.message && !data?.success) throw new Error(data.message);

            setMsg(isEdit ? "Record aggiornato." : "Lavoratore particolare registrato.");
            setShow(false);
            setForm(EMPTY);
            await loadItems();
            await loadStats();
        } catch (err) {
            setError(apiErr(err, "Errore salvataggio."));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Eliminare questo record?")) return;
        try {
            await SafetyParticolariService.delete(id);
            setMsg("Record eliminato.");
            await loadItems();
            await loadStats();
        } catch (err) {
            setError(apiErr(err, "Errore eliminazione."));
        }
    };

    if (!idAzienda) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                Salva prima i dati dell&apos;azienda per gestire i lavoratori particolari.
            </BsAlert>
        );
    }

    const tipoSel = tipi.find(t => String(t.id_tipo_part) === String(form.id_tipo_part));

    return (
        <div>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                    <h5 className="mb-1"><i className="bi bi-person-hearts me-2 text-primary" />Lavoratori particolari</h5>
                    <p className="text-muted small mb-0">Gestanti, minori, lavoro notturno, isolato e disabilità con valutazioni dedicate.</p>
                </div>
                <div className="d-flex gap-2">
                    <Form.Check type="switch" id="solo-attivi" label="Solo attivi" checked={soloAttivi} onChange={e => setSoloAttivi(e.target.checked)} />
                    <Button variant="success" size="sm" onClick={() => openForm()}><i className="bi bi-plus-lg me-1" />Nuovo</Button>
                </div>
            </div>

            {error && <BsAlert variant="danger" className="py-2" onClose={() => setError(null)} dismissible>{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2" onClose={() => setMsg(null)} dismissible>{msg}</BsAlert>}

            {stats && (
                <Row className="g-2 mb-3">
                    <Col xs={6} md={4} lg={2}><Card className="border-0 shadow-sm"><Card.Body className="py-2 px-3"><div className="text-muted small">Attivi</div><div className="fw-bold fs-5">{stats.attivi ?? 0}</div></Card.Body></Card></Col>
                    <Col xs={6} md={4} lg={2}><Card className="border-0 shadow-sm"><Card.Body className="py-2 px-3"><div className="text-muted small">Gestanti/allatt.</div><div className="fw-bold fs-5">{stats.gestanti ?? 0}</div></Card.Body></Card></Col>
                    <Col xs={6} md={4} lg={2}><Card className="border-0 shadow-sm"><Card.Body className="py-2 px-3"><div className="text-muted small">Minori</div><div className="fw-bold fs-5">{stats.minori ?? 0}</div></Card.Body></Card></Col>
                    <Col xs={6} md={4} lg={2}><Card className="border-0 shadow-sm"><Card.Body className="py-2 px-3"><div className="text-muted small">Valut. mancanti</div><div className={`fw-bold fs-5 ${(stats.valutazioni_mancanti ?? 0) > 0 ? "text-danger" : ""}`}>{stats.valutazioni_mancanti ?? 0}</div></Card.Body></Card></Col>
                    <Col xs={6} md={4} lg={2}><Card className="border-0 shadow-sm"><Card.Body className="py-2 px-3"><div className="text-muted small">Scadute</div><div className={`fw-bold fs-5 ${(stats.valutazioni_scadute ?? 0) > 0 ? "text-danger" : ""}`}>{stats.valutazioni_scadute ?? 0}</div></Card.Body></Card></Col>
                </Row>
            )}

            {show && (
                <Card className="mb-3 border-primary">
                    <Card.Body>
                        <Row className="g-2">
                            <Col md={6}>
                                <Form.Label className="small">Lavoratore *</Form.Label>
                                <LavoratoreSearchPicker idAzienda={idAzienda} value={form.id_lavoratore} onChange={id => setForm(f => ({ ...f, id_lavoratore: id }))} />
                            </Col>
                            <Col md={6}>
                                <Form.Label className="small">Condizione particolare *</Form.Label>
                                <Form.Select value={form.id_tipo_part} onChange={e => onTipoChange(e.target.value)} required>
                                    <option value="">— Seleziona —</option>
                                    {tipi.map(t => <option key={t.id_tipo_part} value={t.id_tipo_part}>{t.denominazione}</option>)}
                                </Form.Select>
                                {tipoSel && HELP[tipoSel.codice] && <div className="text-muted small mt-1">{HELP[tipoSel.codice]}</div>}
                            </Col>
                            <Col md={3}><Form.Label className="small">Stato</Form.Label><Form.Select value={form.stato} onChange={e => setForm(f => ({ ...f, stato: e.target.value }))}>{STATI.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</Form.Select></Col>
                            <Col md={3}><Form.Label className="small">Data inizio *</Form.Label><Form.Control type="date" value={form.data_inizio} onChange={e => setForm(f => ({ ...f, data_inizio: e.target.value }))} /></Col>
                            <Col md={3}><Form.Label className="small">Data fine</Form.Label><Form.Control type="date" value={form.data_fine} onChange={e => setForm(f => ({ ...f, data_fine: e.target.value }))} /></Col>
                            <Col md={3}><Form.Label className="small">Esito valutazione</Form.Label><Form.Select value={form.esito_valutazione} onChange={e => setForm(f => ({ ...f, esito_valutazione: e.target.value }))}>{ESITI.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}</Form.Select></Col>
                            <Col md={4}><Form.Label className="small">Responsabile valutazione</Form.Label><Form.Control value={form.responsabile} onChange={e => setForm(f => ({ ...f, responsabile: e.target.value }))} /></Col>
                            <Col md={4}><Form.Label className="small">Data valutazione</Form.Label><Form.Control type="date" value={form.data_valutazione} onChange={e => setForm(f => ({ ...f, data_valutazione: e.target.value }))} /></Col>
                            <Col md={4}><Form.Label className="small">Scadenza revisione</Form.Label><Form.Control type="date" value={form.data_scadenza_valutazione} onChange={e => setForm(f => ({ ...f, data_scadenza_valutazione: e.target.value }))} /></Col>
                            <Col md={12}><Form.Label className="small">Vincoli / misure organizzative</Form.Label><Form.Control as="textarea" rows={2} value={form.vincoli_organizzativi} onChange={e => setForm(f => ({ ...f, vincoli_organizzativi: e.target.value }))} /></Col>
                            <Col md={6}><Form.Label className="small">Allegato valutazione</Form.Label><Form.Control type="file" onChange={e => setFile(e.target.files?.[0] || null)} /></Col>
                            <Col md={12}><Form.Label className="small">Note</Form.Label><Form.Control as="textarea" rows={2} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} /></Col>
                        </Row>
                        <div className="mt-2">
                            <Button variant="primary" size="sm" disabled={saving} onClick={submit} className="me-2">{saving ? <Spinner animation="border" size="sm" /> : "Salva"}</Button>
                            <Button variant="outline-secondary" size="sm" onClick={() => setShow(false)}>Annulla</Button>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {loading ? <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                : items.length === 0 ? <BsAlert variant="info" className="py-2">Nessun lavoratore particolare registrato.</BsAlert>
                : (
                    <div className="table-responsive">
                        <Table hover size="sm" className="align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Lavoratore</th>
                                    <th>Condizione</th>
                                    <th>Periodo</th>
                                    <th>Esito</th>
                                    <th>Revisione</th>
                                    <th>Stato</th>
                                    <th className="text-end">Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(p => (
                                    <tr key={p.id_particolare}>
                                        <td>
                                            <div>{p.lav_cognome} {p.lav_nome}</div>
                                            {p.lav_eta != null && <span className="text-muted small">{p.lav_eta} anni{p.lav_minorenne ? " · minorenne" : ""}</span>}
                                        </td>
                                        <td>
                                            <div>{p.tipo_denominazione}</div>
                                            <span className="text-muted small">{p.tipo_categoria}</span>
                                        </td>
                                        <td className="small">{fmtDate(p.data_inizio)}{p.data_fine ? ` → ${fmtDate(p.data_fine)}` : ""}</td>
                                        <td>{esitoBadge(p.esito_valutazione)}</td>
                                        <td>{statoBadge(p.stato_scadenza, p.giorni_alla_scadenza)}</td>
                                        <td><Badge bg={p.stato === "ATTIVO" ? "primary" : "secondary"}>{p.stato}</Badge></td>
                                        <td className="text-end text-nowrap">
                                            {p.allegato_presente && (
                                                <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => SafetyParticolariService.download(p.id_particolare, `particolare_${p.id_particolare}.pdf`)}>
                                                    <i className="bi bi-download" />
                                                </Button>
                                            )}
                                            <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openForm(p)}><i className="bi bi-pencil" /></Button>
                                            <Button size="sm" variant="outline-danger" onClick={() => handleDelete(p.id_particolare)}><i className="bi bi-trash" /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
        </div>
    );
}
